import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AccountType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { Status } from '~components/Job/Status';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import PricingHistoryButton from '~components/PricingHistory/PricingHistoryButton';
import { FormStateChangeProps } from '~formsShared/types';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { LoadDTO } from '~hooks/useLoad/models';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { JobEditForm, JobEditFormHandler } from '../JobEditForm';
import { processAddOnAssignmentsForJobs } from '../utils/processAddOnAssignmentsForJobs';
import { getLoadsToUpdate, stripRateSideEditsForRematch } from './jobDialogUtils';

interface JobDialogProps {
  isOpen?: boolean;
  job?: Job;
  onClose: (updatedJob?: Job) => void;
}

const JobDialog = observer(({ isOpen, job, onClose }: JobDialogProps) => {
  const { toasterStore, userStore } = useStores();
  const { addLoad, getJobLoads, isUpdating, removeLoad, updateJob, updateLoad } =
    useJob();
  const formRef = useRef<JobEditFormHandler>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isRateRematchWarningOpen, setIsRateRematchWarningOpen] = useState(false);
  const pendingSubmitRef = useRef<(() => void) | null>(null);
  const companyId = getEffectiveUserCompanyId(userStore);

  const handleFormStateChange = useCallback(({ isDirty }: FormStateChangeProps) => {
    setIsFormDirty(isDirty);
  }, []);

  const onFormSubmitSuccess = useCallback(
    async (job: Job) => {
      await formRef.current?.fileAttachmentsOnSubmit?.(job.id);
      onClose(job);
      toasterStore.push(
        alert(t('dispatch.job.updated', { name: job.jobId }), AlertTypes.success),
      );
    },
    [onClose],
  );

  const handleFormSubmit = useCallback(() => {
    const jobId = job?.id;

    if (jobId) {
      formRef.current?.submit?.((data, dirtyFields) => {
        const shouldRematch = Boolean(
          formRef.current?.isOrderOwner &&
          formRef.current?.conditionsChanged &&
          formRef.current?.isRatesDirty,
        );

        const effectiveData = shouldRematch ? stripRateSideEditsForRematch(data) : data;

        const proceed = () => {
          updateJob(jobId, effectiveData, companyId).then((updatedJob) => {
            if (updatedJob) {
              getJobLoads(jobId).then((updatedJobLoads) => {
                const submittedLoads = effectiveData.loads;
                const loadsPromises: Promise<Nullable<LoadDTO> | void>[] = [];

                const loadsToAdd = submittedLoads.filter((submittedLoad) => {
                  return (
                    !submittedLoad.id &&
                    !updatedJobLoads.some((updatedJobLoad) => {
                      return updatedJobLoad.id === submittedLoad.id;
                    })
                  );
                });

                if (loadsToAdd.length) {
                  loadsToAdd.forEach((load) => {
                    loadsPromises.push(addLoad(jobId, load));
                  });
                }

                const presentLoadIds = new Set(updatedJobLoads.map((load) => load.id));
                const deletedLoadIds = (effectiveData.deletedLoadIds ?? []).filter(
                  (loadId) => presentLoadIds.has(loadId),
                );
                deletedLoadIds.forEach((loadId) => {
                  loadsPromises.push(removeLoad(loadId));
                });

                // Only PATCH loads the user actually edited. Untouched loads are
                // left for the BE's job→load cascade to update — otherwise the
                // FE's stale per-load form state overwrites the cascade.
                const dirtyLoads = dirtyFields.loads ?? [];
                const editedLoads = submittedLoads
                  .map((load, idx) => ({ load, dirty: dirtyLoads[idx] }))
                  .filter((entry) => Boolean(entry.dirty));
                const loadsToUpdate = getLoadsToUpdate(
                  editedLoads.map((e) => e.load),
                  updatedJobLoads,
                );

                loadsToUpdate.forEach((load) => {
                  const dirty = editedLoads.find((e) => e.load === load)?.dirty;
                  const serverLoad = updatedJobLoads.find((l) => l.id === load.id);
                  loadsPromises.push(
                    updateLoad(load, serverLoad?.waypoints ?? [], {
                      pickup: Boolean(dirty?.pickUpWaypoints),
                      dropoff: Boolean(dirty?.dropOffWaypoints),
                      staging: Boolean(dirty?.stagingWaypoints),
                    }),
                  );
                });

                const {
                  vendorAddOnAssignments,
                  customerAddOnAssignments,
                  driverAddOnAssignments,
                  customerJobAssignment,
                  vendorJobAssignment,
                } = effectiveData;

                const addOnAssignmentsPromises = [
                  vendorAddOnAssignments && vendorAddOnAssignments.length > 0
                    ? processAddOnAssignmentsForJobs({
                        companyId,
                        id: vendorJobAssignment?.id ?? '',
                        addOnAssignments: vendorAddOnAssignments,
                        accountType: AccountType.VENDOR,
                      })
                    : Promise.resolve(),
                  customerAddOnAssignments && customerAddOnAssignments.length > 0
                    ? processAddOnAssignmentsForJobs({
                        companyId,
                        id: customerJobAssignment?.id ?? '',
                        addOnAssignments: customerAddOnAssignments,
                        accountType: AccountType.CUSTOMER,
                      })
                    : Promise.resolve(),
                  driverAddOnAssignments && driverAddOnAssignments.length > 0
                    ? processAddOnAssignmentsForJobs({
                        companyId,
                        id: jobId,
                        addOnAssignments: driverAddOnAssignments,
                      })
                    : Promise.resolve(),
                ];

                // BE PATCH already committed; downstream ops are best-effort so a rejection doesn't strand the user.
                Promise.allSettled([...loadsPromises, ...addOnAssignmentsPromises]).then(
                  () => {
                    onFormSubmitSuccess(updatedJob);
                  },
                );
              });
            }
          });
        };

        if (shouldRematch) {
          pendingSubmitRef.current = proceed;
          setIsRateRematchWarningOpen(true);
          return;
        }

        proceed();
      });
    }
  }, [companyId, job?.id, onFormSubmitSuccess]);

  const handleConfirmRateRematch = useCallback(() => {
    setIsRateRematchWarningOpen(false);
    pendingSubmitRef.current?.();
    pendingSubmitRef.current = null;
  }, []);

  const handleCancelRateRematch = useCallback(() => {
    setIsRateRematchWarningOpen(false);
    pendingSubmitRef.current = null;
  }, []);

  return (
    <>
      <Dialog
        isOpen={Boolean(isOpen)}
        maxWidth="lg"
        onClose={() => onClose()}
        preventCloseOnBackdropClick
      >
        <DialogHeader onClose={() => onClose()}>
          <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1 }}>
            <Typography component="span" variant="h5" sx={{ flex: 1 }}>
              {t('dispatch.job.edit_job')} <Status status={job?.status} sx={{ ml: 1 }} />
              {job && (
                <Typography component="div" variant="subtitle1">
                  {job.jobId}
                </Typography>
              )}
            </Typography>

            <PricingHistoryButton
              analyticsSurface="job_edit_form"
              jobId={job?.id ?? null}
              subtitle={t('loads.pricing.history.job_ref', { ref: job?.jobId ?? '' })}
            />
          </Box>
        </DialogHeader>

        <DialogContent sx={(theme) => ({ bgcolor: theme.semantic.surfaceSunken })}>
          {job && (
            <JobEditForm
              ref={formRef}
              defaultJob={job}
              onFormStateChange={handleFormStateChange}
            />
          )}
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            data-ui={`page-dispatch__job__job-${job?.jobId}__button`}
            disabled={isUpdating}
            onClick={() => onClose()}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            data-test-id="job-action-btn"
            disabled={isUpdating || !isFormDirty}
            loading={isUpdating}
            onClick={handleFormSubmit}
          >
            {t('actions.submit')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isOpen={isRateRematchWarningOpen}
        onClose={handleCancelRateRematch}
        onConfirm={handleConfirmRateRematch}
        title={t('dispatch.job.form.rate_rematch_warning.title')}
        confirmButtonProps={{
          color: 'primary',
          children: t('dispatch.job.form.rate_rematch_warning.confirm'),
        }}
      >
        <Typography variant="body2">
          {t('dispatch.job.form.rate_rematch_warning.body')}
        </Typography>
      </ConfirmationDialog>
    </>
  );
});

export default JobDialog;
